// Super Admin — Cross-course Video Library (Phase 6 · Iteration 3)
// Aggregated view of every lesson video across the platform with filters,
// bulk-reprocess, storage cost dashboard, and quick links to the source lesson.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Film, Search, RefreshCw, Loader2, HardDrive, Clock, Layers, Zap, CheckCircle2, XCircle, Wrench, Pencil,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const UPLOAD_STATUSES = ["uploading", "uploaded", "ready", "failed"];
const SOURCE_TYPES = ["device_upload", "google_drive", "onedrive", "external_url"];

const STATUS_COLORS = {
  ready: "bg-emerald-100 text-emerald-700",
  uploading: "bg-blue-100 text-blue-700",
  uploaded: "bg-amber-100 text-amber-700",
  failed: "bg-rose-100 text-rose-700",
};

function fmtBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function fmtDuration(sec) {
  if (!sec) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return h ? `${h}h ${m}m` : `${m}m ${s}s`;
}

function Kpi({ label, value, hint, icon }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-2xl font-bold text-slate-800">{value}</div>
      {hint && <div className="mt-0.5 text-[10px] text-slate-400">{hint}</div>}
    </div>
  );
}

export default function AdminVideoLibraryPage() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total_videos: 0, total_bytes: 0, total_original_bytes: 0, total_duration: 0 });
  const [bands, setBands] = useState({});
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 });
  // YouTube duration backfill
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillMode, setBackfillMode] = useState("only_missing"); // or "all"
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const runBackfill = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    // Small chunks (10 per hop) + resilient loop: if ONE hop hits a
    // Cloudflare 524, we DON'T abort — we skip forward `next_skip`
    // and retry the next chunk. Partial progress is kept. Worst-case
    // one CF timeout costs us one 10-video window; the rest still
    // process. User can re-click the button afterwards to sweep any
    // gaps.
    const only_missing = backfillMode === "only_missing";
    let skip = 0;
    let totals = { scanned: 0, updated: 0, failed: 0, skipped: 0, cloudflare_timeouts: 0 };
    let all_results = [];
    try {
      for (let hop = 0; hop < 100; hop += 1) {
        setBackfillResult({ ...totals, results: all_results, in_progress: true, hop });
        let data;
        try {
          const resp = await api.post("/admin/video-tools/backfill-youtube-durations",
            null, { params: { only_missing, skip, max_videos: 10 }, timeout: 90000 });
          data = resp.data;
        } catch (err) {
          // Cloudflare 524 / edge timeout — record it, jump forward,
          // and keep going. We move `skip` by 10 (our batch size) to
          // avoid re-hitting the same slow window.
          totals.cloudflare_timeouts += 1;
          all_results.push({ id: `cf-${skip}`, status: "error",
            title: `Batch at offset ${skip} timed out — CDN cut connection`,
            error: err.message || "cloudflare_timeout" });
          skip += 10;
          if (totals.cloudflare_timeouts >= 5) throw new Error("Too many timeouts — stopping.");
          continue;
        }
        const d = data.data || {};
        totals = {
          scanned: totals.scanned + (d.scanned || 0),
          updated: totals.updated + (d.updated || 0),
          failed: totals.failed + (d.failed || 0),
          skipped: totals.skipped + (d.skipped || 0),
          cloudflare_timeouts: totals.cloudflare_timeouts,
        };
        all_results = all_results.concat(d.results || []);
        if (!d.has_more || (d.scanned || 0) === 0) break;
        skip = d.next_skip;
      }
      setBackfillResult({ ...totals, results: all_results, in_progress: false });
      toast.success(`Done — ${totals.updated} updated, ${totals.failed} failed, ${totals.skipped} skipped${totals.cloudflare_timeouts ? `, ${totals.cloudflare_timeouts} timeouts` : ""}.`);
    } catch (err) { toast.error(formatApiError(err)); setBackfillResult({ ...totals, results: all_results, in_progress: false, error: true }); }
    finally { setBackfilling(false); }
  };
  const [loading, setLoading] = useState(true);
  const [busyRow, setBusyRow] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sourceType, setSourceType] = useState("all");

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search.trim()) params.q = search.trim();
      if (status !== "all") params.status = status;
      if (sourceType !== "all") params.source_type = sourceType;
      const { data } = await api.get("/admin/videos/library", { params });
      setRows(data.data.videos || []);
      setStats(data.data.stats || {});
      setBands(data.data.status_bands || {});
      setPagination(data.data.pagination || { total: 0, page: 1, limit: 20, pages: 1 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }, [search, status, sourceType]);

  useEffect(() => { load(1); }, [load]);

  const reprocess = async (row) => {
    setBusyRow(row.id);
    try {
      await api.post(`/admin/videos/${row.id}/reprocess`);
      toast.success("Video queued for reprocessing.");
      load(pagination.page);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyRow(null); }
  };

  const compressionRatio = useMemo(() => {
    const orig = stats.total_original_bytes || 0;
    const proc = stats.total_bytes || 0;
    if (!orig || !proc) return null;
    return Math.max(0, 100 - Math.round((proc / orig) * 100));
  }, [stats]);

  return (
    <DashboardLayout title="Video Library" breadcrumbs={["Super Admin", "Video Library"]} data-testid="video-library-page">
      <div className="mb-6 text-sm text-slate-600">
        Cross-course inventory of every lesson video — with compression &amp; storage insights.
      </div>

      {/* KPI tiles */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="video-library-stats">
        <Kpi label="Total videos" value={stats.total_videos || 0}
             icon={<Film className="h-3.5 w-3.5 text-[#101876]" />} />
        <Kpi label="Processed storage" value={fmtBytes(stats.total_bytes)}
             hint={`Original: ${fmtBytes(stats.total_original_bytes)}`}
             icon={<HardDrive className="h-3.5 w-3.5 text-emerald-600" />} />
        <Kpi label="Total duration" value={fmtDuration(stats.total_duration)}
             icon={<Clock className="h-3.5 w-3.5 text-amber-600" />} />
        <Kpi label="Avg. compression saved"
             value={compressionRatio == null ? "—" : `${compressionRatio}%`}
             hint="Original → optimized output"
             icon={<Zap className="h-3.5 w-3.5 text-fuchsia-600" />} />
      </div>

      {/* Status bands */}
      <div className="mb-4 flex flex-wrap gap-2" data-testid="video-library-bands">
        <span className="text-xs font-semibold text-slate-500">Status:</span>
        {UPLOAD_STATUSES.map((s) => (
          <span key={s}
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[s] || "bg-slate-100 text-slate-500"}`}>
            {s === "ready" && <CheckCircle2 className="h-3 w-3" />}
            {s === "failed" && <XCircle className="h-3 w-3" />}
            {s}: {bands[s] || 0}
          </span>
        ))}
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="relative min-w-[280px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search title, description, uploader…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(1)}
            data-testid="video-library-search" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); }}>
          <SelectTrigger className="w-[170px]" data-testid="video-library-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {UPLOAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sourceType} onValueChange={(v) => { setSourceType(v); }}>
          <SelectTrigger className="w-[190px]" data-testid="video-library-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {SOURCE_TYPES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <button type="button" onClick={() => load(1)}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          data-testid="video-library-refresh">
          <Layers className="h-3.5 w-3.5" /> Refresh
        </button>
        <button type="button" onClick={() => { setBackfillOpen(true); setBackfillResult(null); }}
          className="inline-flex items-center gap-1.5 rounded-md border border-[#101876] bg-[#101876] px-3 py-2 text-xs font-semibold text-white hover:bg-[#0a1155]"
          data-testid="video-library-backfill-btn">
          <Wrench className="h-3.5 w-3.5" /> Fix YouTube durations
        </button>
      </div>

      <Dialog open={backfillOpen} onOpenChange={(o) => { if (!backfilling) setBackfillOpen(o); }}>
        <DialogContent className="sm:max-w-lg" data-testid="video-library-backfill-dialog">
          <DialogHeader>
            <DialogTitle>Fix YouTube durations</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-slate-600">
              Re-fetches the real duration from YouTube for every already-uploaded
              external-URL video and writes it back to the database. Fixes legacy
              rows where a wrong minute count was saved (e.g. 143 min while the
              real video is only 14:24).
            </p>
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[11px] text-emerald-800">
              <b>Recommended:</b> Set <code>YOUTUBE_API_KEY</code> in
              <code> /app/backend/.env</code> (free key from
              <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="underline"> Google Cloud Console</a>).
              When the key is present, this tool uses the official
              YouTube Data API v3 (100 %-reliable, 1 quota unit per 50
              videos) instead of HTML scraping.
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800">
              <b>Without the API key:</b> YouTube blocks automated
              duration lookups from cloud servers (&quot;Sign in to
              confirm you&apos;re not a bot&quot;). If this backfill
              returns <b>no_duration_detected</b> for every video,
              close this dialog and use the pencil icon next to each
              duration cell in the table below to enter it manually
              (e.g. <code>14:24</code>).
            </div>
            <div className="space-y-2">
              <label className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 p-2 hover:bg-slate-50">
                <input type="radio" name="bf-mode" value="only_missing" checked={backfillMode === "only_missing"}
                       onChange={() => setBackfillMode("only_missing")}
                       data-testid="video-library-backfill-mode-missing" className="mt-1" />
                <div>
                  <div className="text-xs font-bold">Only rows with missing duration (recommended)</div>
                  <div className="text-[10px] text-slate-500">Touches videos where duration is 0 or empty. Safe on live data.</div>
                </div>
              </label>
              <label className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 p-2 hover:bg-slate-50">
                <input type="radio" name="bf-mode" value="all" checked={backfillMode === "all"}
                       onChange={() => setBackfillMode("all")}
                       data-testid="video-library-backfill-mode-all" className="mt-1" />
                <div>
                  <div className="text-xs font-bold">Force refresh every YouTube video</div>
                  <div className="text-[10px] text-slate-500">Overwrites even rows that already have a duration. Use when a bad-data import wrote wrong minutes.</div>
                </div>
              </label>
            </div>
            {backfillResult && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="video-library-backfill-result">
                <div className="mb-1 font-bold">
                  {backfillResult.in_progress ? `In progress — batch ${(backfillResult.hop || 0) + 1}…` : "Result"}
                </div>
                <div>Scanned: <b>{backfillResult.scanned}</b> · Updated: <b className="text-emerald-700">{backfillResult.updated}</b> · Failed: <b className="text-rose-700">{backfillResult.failed}</b> · Skipped: <b>{backfillResult.skipped}</b></div>
                {backfillResult.results?.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                    {backfillResult.results.slice(0, 20).map((r) => (
                      <div key={r.id} className="text-[10px] text-slate-600">
                        <span className={r.status === "updated" ? "text-emerald-700 font-semibold" : r.status === "no_duration_detected" || r.status === "error" ? "text-rose-700" : "text-slate-500"}>
                          {r.status}
                        </span>{" "}
                        {r.title?.slice(0, 50) || r.id}
                        {r.status === "updated" && ` (${r.old_duration}s → ${r.duration_seconds}s)`}
                      </div>
                    ))}
                    {backfillResult.results.length > 20 && (
                      <div className="text-[10px] italic text-slate-400">…{backfillResult.results.length - 20} more.</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setBackfillOpen(false)} disabled={backfilling}
                    className="rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
              Close
            </button>
            <button type="button" onClick={runBackfill} disabled={backfilling}
                    data-testid="video-library-backfill-run"
                    className="inline-flex items-center gap-1 rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0a1155] disabled:opacity-50">
              {backfilling && <Loader2 className="h-3 w-3 animate-spin" />}
              {backfilling ? "Running…" : "Run backfill"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="video-library-table">
        {loading ? (
          <LoadingBlock />
        ) : rows.length === 0 ? (
          <EmptyState title="No videos found" hint="Try adjusting the filters or upload a new video from a lesson." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Video</th>
                  <th className="px-4 py-3">Course / Lesson</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Size</th>
                  <th className="px-4 py-3">Duration</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Uploaded By</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((v) => (
                  <tr key={v.id} className="hover:bg-slate-50" data-testid={`video-row-${v.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{v.video_title}</div>
                      <div className="text-[11px] text-slate-500">{v.output_quality || "—"} · {v.resolution || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-700">{v.course_title || "—"}</div>
                      <div className="text-[11px] text-slate-500">{v.lesson_title || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                        {(v.source_type || "").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div>{fmtBytes(v.processed_file_size)}</div>
                      <div className="text-[11px] text-slate-400">orig: {fmtBytes(v.original_file_size)}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="flex items-center gap-2">
                        <span data-testid={`video-duration-${v.id}`}>{fmtDuration(v.duration_seconds)}</span>
                        <button
                          type="button"
                          onClick={async () => {
                            const cur = fmtDuration(v.duration_seconds);
                            const raw = window.prompt(
                              `Set duration for "${v.video_title || v.id}"\n` +
                              `Format: MM:SS  (or  H:MM:SS  for videos > 1 hr)\n` +
                              `Current: ${cur}`,
                              cur !== "—" ? cur : "",
                            );
                            if (raw === null) return;
                            const trimmed = raw.trim();
                            if (!trimmed) return;
                            const parts = trimmed.split(":").map((p) => p.trim());
                            if (parts.length < 2 || parts.length > 3 || parts.some((p) => !/^\d+$/.test(p))) {
                              toast.error("Enter duration as MM:SS or H:MM:SS (digits only).");
                              return;
                            }
                            const nums = parts.map((n) => parseInt(n, 10));
                            const seconds = parts.length === 3
                              ? nums[0] * 3600 + nums[1] * 60 + nums[2]
                              : nums[0] * 60 + nums[1];
                            if (seconds < 1 || seconds > 24 * 3600) {
                              toast.error("Duration must be between 1s and 24h.");
                              return;
                            }
                            try {
                              await api.patch(`/admin/lesson-videos/${v.id}/duration`, { duration_seconds: seconds });
                              toast.success(`Duration set to ${fmtDuration(seconds)}.`);
                              load();
                            } catch (err) {
                              toast.error(formatApiError(err));
                            }
                          }}
                          title="Edit duration manually"
                          data-testid={`video-duration-edit-${v.id}`}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-[#101876]"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[v.upload_status] || "bg-slate-100 text-slate-500"}`}>
                        {v.upload_status || "—"}
                      </span>
                      {v.processing_error && (
                        <div className="mt-1 text-[10px] text-rose-500" title={v.processing_error}>
                          {v.processing_error.slice(0, 60)}{v.processing_error.length > 60 ? "…" : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-700">{v.created_by_name || "—"}</div>
                      <div className="text-[11px] text-slate-400">{v.created_at?.slice(0, 10)}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {v.source_type !== "external_url" && (
                        <button type="button" onClick={() => reprocess(v)}
                          disabled={busyRow === v.id}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          data-testid={`video-reprocess-${v.id}`}>
                          {busyRow === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                          Reprocess
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-xs text-slate-500" data-testid="video-library-pagination">
          <span>Showing page {pagination.page} of {pagination.pages} · {pagination.total} videos</span>
          <div className="flex gap-2">
            <button disabled={pagination.page <= 1}
              onClick={() => load(pagination.page - 1)}
              className="rounded border border-slate-200 px-2 py-1 font-semibold disabled:opacity-40">Prev</button>
            <button disabled={pagination.page >= pagination.pages}
              onClick={() => load(pagination.page + 1)}
              className="rounded border border-slate-200 px-2 py-1 font-semibold disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
